<script lang="ts">
  import {onDestroy, onMount} from 'svelte';
  import Swal from 'sweetalert2';

  import type {Video} from '@/types/project';
  import {formatDate, convertToHoursMinutes} from '@/utils/common';

  import {useLongPress} from '../../actions/use-long-press';
  import {appEvents, IntegrationService, VideoService} from '@/services';
  import {events} from '@/utils/custom-event-names';
  import {credit, project, isMobile, thumbnail, videos as videoStore, prompt} from '@/store';

  import UploadButton from './UploadButton.svelte';
  import VideoMetadata from './VideoMetadata.svelte';
  import DeleteVideoDialog from './Dialogs/DeleteVideoDialog.svelte';
  import UploadFromLivestreamDialog from './Dialogs/UploadFromLivestreamDialog.svelte';
  import UploadFromYoutubeDialog from './Dialogs/UploadFromYoutubeDialog.svelte';
  import UploadFromLocalDialog from './Dialogs/UploadFromLocalDialog.svelte';
  import UploadFromIntegrationsDialog from './Dialogs/UploadFromIntegrationsDialog.svelte';
  import DraftsDialog from './Dialogs/DraftsDialog.svelte';
  import RenameVideoDialog from './Dialogs/RenameVideoDialog.svelte';
  import {tooltip} from '@/actions/tippy-action';
  import Dialog from '@/components/dialog/Dialog.svelte';
  import VideoSource from './VideoSource.svelte';
  import {UploadSource} from '@/types/common';
  import {Integration, IntegrationUser} from '@/types/integrations';
  import UploadProgress from './UploadProgress.svelte';
  import {selVideo, vidDialogOpen} from '../thumbPreview/viddialog-store';

  let formatted: string;
  $: formatted = convertToHoursMinutes($credit.amount);

  let videoPage = 1;
  let videoPageLimit = 12;
  let videoFilesLength = 0;

  let observingElement: HTMLDivElement;
  let isLoadingVideos = true;
  let isMaxVideoPage = false;
  let isUploading: boolean = false;
  let source: UploadSource;
  let integration: Integration;
  let userIntegration: IntegrationUser | undefined;

  let isUploadDropdownOpen = false;
  let isUploadDropdownOpen2 = false;

  let isUploadLiveStreamDialogOpen = false;
  let isUploadYoutubeDialogOpen = false;
  let isUploadLocalDialogOpen = false;
  let isUploadIntegrationsDialogOpen = false;
  let isDeleteDialogOpen = false;
  let isVideoDialogOpen = false;
  let isDraftsDialogOpen = false;
  let isRenameDialogOpen = false;
  let activeIndex: number = -1;
  let isDownloading: boolean = false;
  let is1080pAvailableDialogOpen = false;

  let videos: Video[] = [];
  let queuedVideos: any[] = [];

  let selectedVideo: Video | null;
  let selectedRenameVideo: Video;

  let isSelectingVideos = false;
  let selectedVideos: Video[] = [];
  let percentage: number = 0;

  let message: string = 'Processing Video';
  let status: string = 'info';

  let dropdownListener = (event: Event) => {};
  const service = new VideoService();

  const closeUploadDropdown = () => {
    isUploadDropdownOpen = false;
    isUploadDropdownOpen2 = false;
  };

  function listenToDropdown(dropdownBoolean: boolean, eventListener: (event: Event) => void) {
    dropdownListener = eventListener;
    (dropdownBoolean && setTimeout(() => window.addEventListener('click', eventListener))) ||
      window.removeEventListener('click', eventListener);
  }

  function escapeDialogListener(event: KeyboardEvent) {
    const key = event.key;
    if (key !== 'Escape') return;
    isDraftsDialogOpen =
      isDeleteDialogOpen =
      isUploadLiveStreamDialogOpen =
      isUploadYoutubeDialogOpen =
      isUploadLocalDialogOpen =
        false;
    selectedVideo = null;
  }

  async function selectVideo(video: Video) {
    selVideo.set(video);
    vidDialogOpen.set(true);
    // Make one request
    await service.getVideo(video.id, true).then(video => {
      project.setVideo(video);
      thumbnail.setVideo(video);
      selVideo.set(video);
    });

    // if (!isSelectingVideos) return selectedVideo = video;

    // const isSelected = selectedVideos.filter((value) => value === video).length > 0;
    // if (isSelected) return selectedVideos = selectedVideos.filter((value) => value !== video);

    // selectedVideos.push(video);
    // selectedVideos = selectedVideos;
  }

  function setActiveIndex(event: CustomEvent) {
    activeIndex = event.detail.index;
  }

  function setSource(event: CustomEvent) {
    source = event.detail.source;
  }

  async function setIntegration(event: CustomEvent) {
    integration = event.detail.integration;
    userIntegration = await new IntegrationService().getUserIntegration(integration?.id);
    isUploadIntegrationsDialogOpen = true;
  }

  $: if (window.innerHeight > 1080) {
    videoPageLimit = 24;
  }

  const showErrorModal = (data: any) => {
    Swal.fire({
      titleText: 'Upload Error',
      html:
        '<p>It looks like we encountered an error uploading your video at this time. Please try again later.</p><br>' +
        '<p>Alternatively, you can upload from your computer if you have a local copy if you want to use the video now.</p>',
      footer: '<a href="https://sermonshots.com/contact">Contact us if you have any questions.</a>',
      icon: 'error'
    });
    isUploading = false;
    isUploadLocalDialogOpen = false;
    isUploadYoutubeDialogOpen = false;
    isUploadIntegrationsDialogOpen = false;
  };

  const videosSubscription = videoStore.subscribe(value => {
    videos = value.files;
    queuedVideos = value.queued;
    videoFilesLength = videos.length;
    selectedVideos = selectedVideos.filter(video => videos.includes(video));
  });

  let isLoadingNewVideoPage = false;

  const pageObserver = new IntersectionObserver(
    entries => {
      const entry = entries[0] || null;
      if (!entry || !entry.isIntersecting || isMaxVideoPage || isLoadingNewVideoPage) return;
      const nextPage = videoPage + 1;
      if (nextPage <= 1) return;
      isLoadingNewVideoPage = true;
      videoStore.getVideoList(nextPage, videoPageLimit).then(({reachedMaxPage}) => {
        isLoadingNewVideoPage = false;
        if (isMaxVideoPage) return;
        if (reachedMaxPage) return (isMaxVideoPage = true);
        videoPage = nextPage;
      });
    },
    {
      rootMargin: '0px',
      threshold: 0.1
    }
  );

  const uploadCompleteListener = async (data: {video: Video; currentVideo?: Video}) => {
    isDownloading = false;
    isUploading = false;
    isUploadLocalDialogOpen = false;
    isUploadYoutubeDialogOpen = false;
    isUploadIntegrationsDialogOpen = false;

    if (data.video) {
      const newVideo = service.formatObject(data.video);
      await videoStore.addVideo(newVideo);
      selectVideo(newVideo);
    }

    if (data.currentVideo) {
      const newVideo = service.formatObject(data.currentVideo);
      await videoStore.replaceVideo(newVideo);
    }
  };

  onMount(() => {
    // Swal.fire(
    //   'Degraded Performance',
    //   'We are having performance issues today, which may affect speed and certain features. Our team is working to resolve this as quickly as possible — thank you for bearing with us!',
    //   'warning'
    // );
    videoStore.getVideoList(videoPage, videoPageLimit).then(() => (isLoadingVideos = false));
    window.addEventListener('keydown', escapeDialogListener);
    pageObserver.observe(observingElement);
    prompt.resetState();

    appEvents.on('upload-error', data => {
      showErrorModal(data);
    });

    appEvents.on('youtube-download-failed', data => {
      showErrorModal(data);
    });

    appEvents.on('action-started', data => {
      isUploading = true;
    });

    appEvents.on('upload-complete', uploadCompleteListener);

    if ($project.video) {
      selectedVideo = $project.video;
    }
  });

  onDestroy(() => {
    window.removeEventListener('keydown', escapeDialogListener);
    window.removeEventListener('click', dropdownListener);
    window.dispatchEvent(new Event(events.closeSideNav));
    appEvents.off('upload-complete', uploadCompleteListener);
    videosSubscription();
    pageObserver.disconnect();
  });

  $: {
    listenToDropdown(isUploadDropdownOpen, closeUploadDropdown);
    listenToDropdown(isUploadDropdownOpen2, closeUploadDropdown);
    isVideoDialogOpen = selectedVideo !== null;

    if (selectedVideo) videos = videos; // re-render video list if a selected video has been modified or called to be rerendered
  }

  const deselect = () => {
    isSelectingVideos = false;
    selectedVideos = [];
  };

  let openDropdownIndex = -1;

  function openDropDown(index: number) {
    if (openDropdownIndex !== -1) window.removeEventListener('click', closeDropdown);
    setTimeout(() => {
      openDropdownIndex = index;
      window.addEventListener('click', closeDropdown);
    });
  }

  function closeDropdown() {
    openDropdownIndex = -1;
    window.removeEventListener('click', closeDropdown);
  }

  function deleteVideo(video: any) {
    selectedVideos = [video];
    isDeleteDialogOpen = true;
  }

  function renameVideo(video: any) {
    selectedRenameVideo = video;
  }

  const animationDelay = 500;
</script>

<div class="flex h-full w-full justify-center overflow-auto p-4 px-8 pb-12">
  <div class="h-full w-full max-w-[90rem]">
    <div class="flex h-auto w-full gap-4 pt-2">
      <!-- {#if !isSelectingVideos}
            <button class="text-zinc-600 text-sm" on:click={() => isSelectingVideos = true }>
                Select
            </button>
            {/if} -->
      <div class="relative ml-auto flex w-auto flex-col gap-4 lg:w-auto lg:flex-row">
        <div
          class="flex items-center justify-center rounded border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-2 text-xs text-gray-600 dark:border-zinc-500 dark:bg-zinc-700 dark:text-zinc-100 lg:text-sm"
        >
          {#if $credit.loaded}
            <span><span class="font-bold">Upload Credits:&nbsp;</span> {formatted} left </span>
            <i
              use:tooltip={'Hours are replenished on the 1st of every month'}
              class="fa-solid fa-circle-info ml-2 text-xs"
            ></i>
          {:else}
            Loading credits...
          {/if}
        </div>
        <UploadButton
          bind:isUploadDropdownOpen
          bind:isUploadLiveStreamDialogOpen
          bind:isUploadYoutubeDialogOpen
          bind:isUploadLocalDialogOpen
          bind:isUploading
          on:set-source={setSource}
          on:set-integration={setIntegration}
        />
      </div>
    </div>
    <div class="flex gap-2 py-4">
      {#if isSelectingVideos}
        <div class="flex items-center gap-2 text-xs transition-all">
          <span class="font-medium text-gray-400">{selectedVideos.length} selected</span>
          <div class="text-gray-400">|</div>
          <div class="flex items-center gap-4">
            <button on:click={() => deselect()} class="font-medium text-gray-800 underline dark:text-zinc-100"
              >Deselect</button
            >
            <button
              on:click={() => (selectedVideos.length > 0 ? (isDeleteDialogOpen = true) : null)}
              class="border bg-transparent {selectedVideos.length > 0
                ? 'border-red-500 text-red-500'
                : 'border-zinc-600 text-zinc-600'} flex items-center gap-1 rounded-md px-2 py-1"
            >
              <i class="fa-regular fa-trash"></i>
              <span>Delete</span>
            </button>
          </div>
        </div>
      {/if}
      {#if !$isMobile}
        <div class="ml-auto">
          <button
            on:click={() => (isDraftsDialogOpen = true)}
            class="bg-white-100 transtion-all rounded-lg border border-zinc-200 px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-100 dark:border-zinc-600 dark:text-zinc-700"
            >Show Most Recent Drafts</button
          >
        </div>
      {/if}
    </div>
    <div class="h-auto min-h-[calc(100%-7rem)] w-full pb-12 pt-4">
      {#if isLoadingVideos}
        <div class="flex max-h-full w-full flex-wrap gap-6 px-2">
          {#each Array(parseInt((Math.random() * 11 + 3).toFixed(0))) as _, i}
            <div class="w-full lg:w-[calc(25%-1.5rem)]">
              <div
                class="h-auto w-full animate-pulse overflow-hidden rounded-xl bg-zinc-300"
                style="animation-delay: {animationDelay * i}ms;"
              >
                <div class="aspect-video h-full w-full"></div>
              </div>
              <div class="w-full">
                <div
                  class="mt-1 animate-pulse rounded-md bg-zinc-300 text-sm"
                  style="width: {(Math.random() * 80 + 20).toFixed(0)}%; animation-delay: {animationDelay * i}ms;"
                >
                  &nbsp;
                </div>
                <div
                  class="mt-1 animate-pulse rounded-md bg-zinc-300 text-xs lg:text-sm"
                  style="width: {(Math.random() * 90 + 10).toFixed(0)}%; animation-delay: {animationDelay * i}ms;"
                >
                  &nbsp;
                </div>
              </div>
            </div>
          {/each}
        </div>
      {:else if videos.length <= 0 && queuedVideos.length <= 0}
        <div class="flex h-full w-full items-center justify-center">
          <div class="relative flex flex-col items-center justify-center gap-4">
            <img
              class="max-w-[400px] pt-10 opacity-50"
              src="https://storage.googleapis.com/sermonshots-homepage/v2/images/SS%20-%20Flow%20-%20Platform%20Redesign.png"
              alt="clips"
            />
            <h1 class="text-xl font-bold dark:text-zinc-100">Upload your first sermon to get started.</h1>
            <UploadButton
              bind:isUploadDropdownOpen={isUploadDropdownOpen2}
              bind:isUploadLiveStreamDialogOpen
              bind:isUploadYoutubeDialogOpen
              bind:isUploadLocalDialogOpen
              bind:isUploading
              on:set-source={setSource}
              on:set-integration={setIntegration}
            />
          </div>
        </div>
      {:else}
        <div class="flex max-h-full w-full flex-wrap gap-4 px-2">
          {#if isUploading}
            <UploadProgress bind:isUploading />
          {/if}
          {#each videos as video, index (video.id)}
            <div class="w-full lg:w-[calc(25%-1.5rem)]">
              <button
                use:useLongPress={{
                  onLongPress: () => (isSelectingVideos = true),
                  onClick: () => selectVideo(video),
                  addCtrl: true
                }}
                class="relative h-auto w-full overflow-hidden rounded-xl border-2 border-transparent bg-zinc-100 transition-all hover:border-primary dark:bg-zinc-700"
              >
                <img
                  class="aspect-video w-full object-cover"
                  src={video.preview}
                  alt="video-thumbnail"
                  loading="lazy"
                />
                {#if isSelectingVideos}
                  <input
                    class="absolute right-[0.5rem] top-[0.5rem] accent-rose-500"
                    type="checkbox"
                    checked={selectedVideos.filter(value => value == video).length > 0}
                  />
                {/if}
              </button>
              <div class="relative w-full">
                <div class="flex w-full overflow-hidden">
                  <button
                    use:useLongPress={{
                      onLongPress: () => (isSelectingVideos = true),
                      onClick: () => selectVideo(video),
                      addCtrl: true
                    }}
                    class="truncate text-start"
                  >
                    <h1 class="truncate text-sm font-semibold text-dark dark:text-zinc-200">{video.title}</h1>
                    <span class="flex justify-start gap-2">
                      <h2 class="truncate pt-1 text-xs text-gray-500 lg:text-sm">{formatDate(video.dateAdded)}</h2>
                      <VideoSource bind:source={video.source} />
                    </span>
                  </button>
                  <div class="ml-auto flex items-center px-2">
                    <button class="px-2" on:click={() => openDropDown(openDropdownIndex === index ? -1 : index)}>
                      <i class="fa-solid fa-ellipsis-vertical text-xl"></i>
                    </button>
                  </div>
                </div>
                <div
                  class="absolute z-20 w-full {openDropdownIndex === index
                    ? ''
                    : 'hidden'} grid rounded-lg border border-zinc-200 bg-white text-sm shadow-md"
                >
                  <button on:click={() => renameVideo(video)} class="px-4 py-2 hover:text-rose-500">
                    Rename Video
                  </button>
                  <button on:click={() => deleteVideo(video)} class="px-4 py-2 hover:text-rose-500">
                    Delete Video
                  </button>
                </div>
                {#if video.quality}
                  <VideoMetadata
                    {video}
                    {index}
                    {activeIndex}
                    {isDownloading}
                    on:set-active-index={setActiveIndex}
                    on:toggle-1080-dialog={() => (is1080pAvailableDialogOpen = true)}
                  />
                {/if}
              </div>
            </div>
          {/each}
        </div>
      {/if}
      <div bind:this={observingElement} class="pointer-events-none h-4 w-full p-4 opacity-0"></div>
    </div>
  </div>
</div>

<!-- Dialogs -->

<DeleteVideoDialog bind:isDeleteDialogOpen bind:selectedVideos />

<UploadFromLivestreamDialog bind:isUploadLiveStreamDialogOpen />

<UploadFromYoutubeDialog
  bind:isUploadYoutubeDialogOpen
  bind:videoFilesLength
  bind:isUploading
  bind:source
  on:toggle-1080-dialog={() => (is1080pAvailableDialogOpen = true)}
/>

<UploadFromLocalDialog bind:isUploadLocalDialogOpen bind:isUploading />

<UploadFromIntegrationsDialog
  bind:isUploadIntegrationsDialogOpen
  bind:integration
  bind:userIntegration
  bind:isUploading
/>

<DraftsDialog bind:isDraftsDialogOpen />

<RenameVideoDialog bind:video={selectedRenameVideo} on:rename={() => (videos = videos)} />

<Dialog bind:isDialogOpen={is1080pAvailableDialogOpen} useIfShow={true}>
  <div class="pointer-events-none flex h-full w-full items-end justify-center lg:items-center">
    <div
      class="pointer-events-auto h-auto w-full min-w-[280px] rounded-2xl border border-gray-300 bg-white shadow-lg dark:border-zinc-600 dark:bg-zinc-700 lg:max-w-[680px]"
    >
      <div class="flex p-6 text-black dark:text-zinc-200">
        <div class="flex items-center gap-2 py-1.5">
          <h1 class="text-3xl font-bold">1080p Currently Unavailable</h1>
        </div>
        <button
          on:click={() => (is1080pAvailableDialogOpen = false)}
          class="my-2 ml-auto rounded-lg px-2 hover:bg-zinc-100 dark:hover:bg-zinc-600"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
      <hr class="border-t border-t-gray-300 dark:border-zinc-600" />
      <div class="grid p-10">
        <div class="py-2">
          <p class="font-bold">Why can't I upload 1080p?</p>
          <p class="py-4 text-gray-800">
            YouTube sometimes takes extra time to make the highest quality version of your video available for us to
            upload for you.
          </p>
        </div>
        <div class="py-2">
          <p class="font-bold">When will it be available?</p>
          <p class="py-4 text-gray-800">
            It's impossible to predict but most videos are ready in 24 hours and typically it's never more than 48
            hours.
          </p>
        </div>
        <div class="py-2">
          <p class="font-bold">What else can be done?</p>
          <p class="py-4 text-gray-800">
            You can <span class="font-bold">still upload</span> the best quality version. This will allow you to get a headstart
            on creating everything. Then when the 1080p version is ready, you can use the option to upload it. This will
            replace the lower quality version before you download any videos.
          </p>
        </div>
      </div>
    </div>
  </div>
</Dialog>

<style>
  @media (max-width: 768px) {
    .side-navigation {
      width: 100%; /* Make sidebar take full width */
      display: none; /* Hide the sidebar by default */
    }

    .main-content {
      width: 100%; /* Ensure the main content takes up full width on mobile */
    }

    .navbar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 999;
    }
  }

  @media (min-width: 769px) {
    .side-navigation {
      width: 300px;
      display: block;
    }

    .main-content {
      width: calc(100% - 300px);
    }
  }
</style>
